import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
	login,
	loginWithGoogle,
	loginWithApple,
	isLoggedIn,
	requestPasswordReset,
	verifyResetCode,
	changePassword,
	getCurrentSubscription,
} from "../services/authService";
import { GOOGLE_CLIENT_ID, APPLE_CLIENT_ID, APPLE_REDIRECT_URI } from "../services/apiConfig";

const STEPS = {
	LOGIN: "login",
	FORGOT_PASSWORD: "forgot_password",
	ENTER_CODE: "enter_code",
	NEW_PASSWORD: "new_password",
};

function Login() {
	const navigate = useNavigate();

	const navigateAfterLogin = async () => {
		const result = await getCurrentSubscription();
		if (result.success && result.subscription) {
			const proTierIds = [4, 6];
			if (proTierIds.includes(result.subscription.idAbonnementType)) {
				navigate("/coach");
				return;
			}
		}
		navigate("/dashboard");
	};

	const [step, setStep] = useState(STEPS.LOGIN);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [resetCode, setResetCode] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [loading, setLoading] = useState(false);
	const googleButtonRef = useRef(null);

	const handleGoogleResponse = useCallback(async (response) => {
		setError("");
		setLoading(true);

		const result = await loginWithGoogle(response.credential);

		setLoading(false);

		if (result.success) {
			navigateAfterLogin();
		} else {
			setError(result.error);
		}
	}, [navigate]);

	const handleAppleSignIn = useCallback(async () => {
		try {
			if (!window.AppleID) {
				setError("Apple Sign-In n'est pas disponible");
				return;
			}

			window.AppleID.auth.init({
				clientId: APPLE_CLIENT_ID,
				scope: "name email",
				redirectURI: APPLE_REDIRECT_URI,
				usePopup: true,
			});

			const response = await window.AppleID.auth.signIn();

			setError("");
			setLoading(true);

			const fullName = response.user
				? `${response.user.name?.firstName || ""} ${response.user.name?.lastName || ""}`.trim() || null
				: null;
			const email = response.user?.email || null;

			const result = await loginWithApple(
				response.authorization.id_token,
				fullName,
				email
			);

			setLoading(false);

			if (result.success) {
				navigate("/coach");
			} else {
				setError(result.error);
			}
		} catch (error) {
			setLoading(false);
			if (error.error !== "popup_closed_by_user") {
				console.error("Apple sign-in error:", error);
				setError("Échec de la connexion Apple");
			}
		}
	}, [navigate]);

	useEffect(() => {
		// Redirect if already logged in
		if (isLoggedIn()) {
			navigateAfterLogin();
			return;
		}

		// Initialize Google Identity Services
		const initGoogleSignIn = () => {
			if (window.google?.accounts?.id) {
				window.google.accounts.id.initialize({
					client_id: GOOGLE_CLIENT_ID,
					callback: handleGoogleResponse,
				});

				if (googleButtonRef.current) {
					window.google.accounts.id.renderButton(googleButtonRef.current, {
						theme: "outline",
						size: "large",
						width: "100%",
						text: "signin_with",
						locale: "fr",
					});
				}
			}
		};

		// GIS script may not be loaded yet
		if (window.google?.accounts?.id) {
			initGoogleSignIn();
		} else {
			const interval = setInterval(() => {
				if (window.google?.accounts?.id) {
					clearInterval(interval);
					initGoogleSignIn();
				}
			}, 100);
			return () => clearInterval(interval);
		}
	}, [navigate, handleGoogleResponse]);

	const handleLogin = async (e) => {
		e.preventDefault();
		setError("");
		setLoading(true);

		const result = await login(email, password);

		setLoading(false);

		if (result.success) {
			navigateAfterLogin();
		} else {
			setError(result.error);
		}
	};

	const handleForgotPassword = async (e) => {
		e.preventDefault();
		setError("");
		setSuccess("");
		setLoading(true);

		const result = await requestPasswordReset(email);

		setLoading(false);

		if (result.success) {
			setSuccess("Un code a été envoyé à votre adresse email");
			setStep(STEPS.ENTER_CODE);
		} else {
			setError(result.error);
		}
	};

	const handleVerifyCode = async (e) => {
		e.preventDefault();
		setError("");
		setLoading(true);

		const result = await verifyResetCode(email, resetCode);

		setLoading(false);

		if (result.success) {
			setStep(STEPS.NEW_PASSWORD);
		} else {
			setError(result.error);
		}
	};

	const handleChangePassword = async (e) => {
		e.preventDefault();
		setError("");

		if (newPassword.length < 6) {
			setError("Le mot de passe doit contenir au moins 6 caractères");
			return;
		}

		if (newPassword !== confirmPassword) {
			setError("Les mots de passe ne correspondent pas");
			return;
		}

		setLoading(true);

		const result = await changePassword(email, resetCode, newPassword);

		setLoading(false);

		if (result.success) {
			setSuccess("Mot de passe modifié avec succès");
			setStep(STEPS.LOGIN);
			setPassword("");
			setResetCode("");
			setNewPassword("");
			setConfirmPassword("");
		} else {
			setError(result.error);
		}
	};

	const goBackToLogin = () => {
		setStep(STEPS.LOGIN);
		setError("");
		setSuccess("");
		setResetCode("");
		setNewPassword("");
		setConfirmPassword("");
	};

	return (
		<div className="page">
			<div className="login-container">
				<div className="login-card">
					<h1 className="login-title">
						{step === STEPS.LOGIN && "Connexion"}
						{step === STEPS.FORGOT_PASSWORD && "Mot de passe oublié"}
						{step === STEPS.ENTER_CODE && "Vérification"}
						{step === STEPS.NEW_PASSWORD && "Nouveau mot de passe"}
					</h1>

					{error && <div className="login-error">{error}</div>}
					{success && <div className="login-success">{success}</div>}

					{step === STEPS.LOGIN && (
						<>
						<form onSubmit={handleLogin} className="login-form">
							<div className="form-group">
								<label htmlFor="email">Email</label>
								<input
									type="email"
									id="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="votre@email.com"
									required
									disabled={loading}
								/>
							</div>

							<div className="form-group">
								<label htmlFor="password">Mot de passe</label>
								<input
									type="password"
									id="password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									placeholder="Votre mot de passe"
									required
									disabled={loading}
								/>
							</div>

							<button
								type="submit"
								className="login-button"
								disabled={loading}
							>
								{loading ? "Connexion..." : "Se connecter"}
							</button>

							<button
								type="button"
								className="forgot-password-link"
								onClick={() => setStep(STEPS.FORGOT_PASSWORD)}
							>
								Mot de passe oublié ?
							</button>
						</form>

						<div className="google-signin-separator">
							<span className="separator-line"></span>
							<span className="separator-text">ou</span>
							<span className="separator-line"></span>
						</div>

						<div className="google-signin-container" ref={googleButtonRef}></div>


						<button
							type="button"
							className="apple-signin-button"
							onClick={handleAppleSignIn}
							disabled={loading}
						>
							<svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
								<path d="M13.714 9.384c-.023-2.426 1.98-3.591 2.07-3.648-1.126-1.647-2.88-1.872-3.505-1.898-1.492-.152-2.914.88-3.672.88-.756 0-1.926-.858-3.164-.834-1.628.024-3.13.948-3.968 2.41-1.692 2.934-.434 7.284 1.216 9.666.806 1.166 1.768 2.476 3.032 2.43 1.216-.05 1.676-.786 3.148-.786 1.472 0 1.884.786 3.172.762 1.308-.024 2.138-1.19 2.938-2.36.926-1.354 1.308-2.664 1.332-2.732-.03-.014-2.554-.98-2.579-3.89zM11.286 2.076c.67-.812 1.122-1.94 1-3.076-. 964.04-2.134.642-2.826 1.43-.62.716-1.162 1.862-1.016 2.96 1.076.084 2.174-.548 2.842-1.314z"/>
							</svg>
							<span>Se connecter avec Apple</span>
						</button>
						</>
					)}

					{step === STEPS.FORGOT_PASSWORD && (
						<form onSubmit={handleForgotPassword} className="login-form">
							<p className="login-description">
								Entrez votre email pour recevoir un code de réinitialisation
							</p>

							<div className="form-group">
								<label htmlFor="reset-email">Email</label>
								<input
									type="email"
									id="reset-email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="votre@email.com"
									required
									disabled={loading}
								/>
							</div>

							<button
								type="submit"
								className="login-button"
								disabled={loading}
							>
								{loading ? "Envoi..." : "Envoyer le code"}
							</button>

							<button
								type="button"
								className="back-link"
								onClick={goBackToLogin}
							>
								Retour à la connexion
							</button>
						</form>
					)}

					{step === STEPS.ENTER_CODE && (
						<form onSubmit={handleVerifyCode} className="login-form">
							<p className="login-description">
								Entrez le code à 6 chiffres reçu par email
							</p>

							<div className="form-group">
								<label htmlFor="code">Code de vérification</label>
								<input
									type="text"
									id="code"
									value={resetCode}
									onChange={(e) => setResetCode(e.target.value)}
									placeholder="000000"
									maxLength={6}
									className="code-input"
									required
									disabled={loading}
								/>
							</div>

							<button
								type="submit"
								className="login-button"
								disabled={loading}
							>
								{loading ? "Vérification..." : "Vérifier le code"}
							</button>

							<button
								type="button"
								className="back-link"
								onClick={goBackToLogin}
							>
								Annuler
							</button>
						</form>
					)}

					{step === STEPS.NEW_PASSWORD && (
						<form onSubmit={handleChangePassword} className="login-form">
							<p className="login-description">
								Choisissez votre nouveau mot de passe
							</p>

							<div className="form-group">
								<label htmlFor="new-password">Nouveau mot de passe</label>
								<input
									type="password"
									id="new-password"
									value={newPassword}
									onChange={(e) => setNewPassword(e.target.value)}
									placeholder="Minimum 6 caractères"
									required
									disabled={loading}
								/>
							</div>

							<div className="form-group">
								<label htmlFor="confirm-password">Confirmer le mot de passe</label>
								<input
									type="password"
									id="confirm-password"
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									placeholder="Confirmez votre mot de passe"
									required
									disabled={loading}
								/>
							</div>

							<button
								type="submit"
								className="login-button"
								disabled={loading}
							>
								{loading ? "Modification..." : "Changer le mot de passe"}
							</button>

							<button
								type="button"
								className="back-link"
								onClick={goBackToLogin}
							>
								Annuler
							</button>
						</form>
					)}
				</div>
			</div>
		</div>
	);
}

export default Login;
